import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { sendToParent } from '../../../frame-messages';
import { AppParamsContext, IAppParams } from '../../AppParams';
import { BRAND_PRESENCE_BASE_PATH, stripQueryString } from './brandPresencePaths';

/**
 * Set by the area's navigation around the screens it renders. Inside the area the navigation's own
 * sync reports every address, so the sync a screen mounts for its own prefix leaves the report to
 * it: two reports of one address would be two entries in the host page's history.
 */
export const BrandPresenceAreaRouteSyncContext = React.createContext(false);

interface IUseGovernanceRouteSyncProps {
  embedPrefix: string;
  appPrefix: string;
  defaultEmbedPath?: string;
  skipBasePathUpdate?: boolean;
}

function normalizePrefix(prefix: string): string {
  return prefix.endsWith('/') ? prefix.slice(0, -1) : prefix;
}

export function useBrandPresenceRouteSync(props: IUseGovernanceRouteSyncProps) {
  const { defaultEmbedPath, skipBasePathUpdate } = props;
  const embedPrefix = React.useMemo(() => normalizePrefix(props.embedPrefix), [props.embedPrefix]);
  const appPrefix = React.useMemo(() => normalizePrefix(props.appPrefix), [props.appPrefix]);
  const lastHandledRequestedPathRef = React.useRef<string | undefined>(undefined);
  const previousRequestedPathRef = React.useRef<string | undefined>(undefined);

  const appParams = React.useContext(AppParamsContext) as IAppParams | null;
  const requestedPath = appParams?.pageOptions?.brandPresencePath || appParams?.pageOptions?.governancePath;
  const reportedByArea = React.useContext(BrandPresenceAreaRouteSyncContext);

  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (reportedByArea || !location.pathname.startsWith(embedPrefix)) {
      return;
    }

    const suffix = location.pathname.substring(embedPrefix.length);
    if (skipBasePathUpdate && (!suffix || suffix === '/')) {
      return;
    }

    // Only a path the host page is not on yet is reported: while the paths agree, the query
    // travels through `update-query`, and reporting the frame's own query here would overwrite the
    // host page's with a copy nothing keeps current.
    const mappedAppPathname = `${appPrefix}${suffix}`;
    if (typeof requestedPath === 'string' && stripQueryString(requestedPath) === mappedAppPathname) {
      return;
    }

    // One navigation is one message, and one entry in the host page's history: the path with the
    // query a link or a Back names on it. The host page pushes a reported path as it is; a query sent
    // after it would be a second entry, and when both arrive before the host page renders, the host
    // page resolves that query against the path it is leaving. At the handover — the host page on
    // the area's bare path — the host page lays its own query onto a path inside the area, so the
    // path goes alone there.
    const hostAtHandover =
      typeof requestedPath === 'string' &&
      stripQueryString(requestedPath).replace(/\/$/, '') === BRAND_PRESENCE_BASE_PATH &&
      mappedAppPathname.startsWith(`${BRAND_PRESENCE_BASE_PATH}/`);
    sendToParent({
      type: 'update-path',
      path: hostAtHandover ? mappedAppPathname : `${mappedAppPathname}${location.search}`,
    });
  }, [location.pathname, location.search, embedPrefix, appPrefix, skipBasePathUpdate, requestedPath, reportedByArea]);

  React.useEffect(() => {
    if (location.pathname !== embedPrefix) {
      return;
    }

    const hasRequestedPathChanged = requestedPath !== previousRequestedPathRef.current;
    if (!hasRequestedPathChanged) {
      return;
    }

    if (typeof requestedPath === 'string' && requestedPath === lastHandledRequestedPathRef.current) {
      return;
    }

    if (typeof requestedPath !== 'string' || !requestedPath.startsWith(appPrefix)) {
      if (defaultEmbedPath) {
        navigate(defaultEmbedPath, { replace: true });
      }

      lastHandledRequestedPathRef.current = undefined;
      return;
    }

    const suffix = requestedPath.substring(appPrefix.length);
    const nextPath = `${embedPrefix}${suffix}`;
    if (nextPath !== location.pathname) {
      navigate(nextPath, { replace: true });
    }

    lastHandledRequestedPathRef.current = requestedPath;
  }, [requestedPath, location.pathname, navigate, embedPrefix, appPrefix, defaultEmbedPath]);

  React.useEffect(() => {
    previousRequestedPathRef.current = requestedPath;
  }, [requestedPath]);
}

export const useGovernanceRouteSync = useBrandPresenceRouteSync;
